import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, CanActivateFn, Params, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { ContextStateService } from '@core/services/context-state.service';
import { AskemoModuleAndProjectIdViewModel, ContextApiService } from '@core/services/context.api.service';
import { ModuleRouteContextService } from '@core/services/modules/module-route-context.service';
import { Observable, catchError, filter, map, of, switchMap, take } from 'rxjs';

/**
 * Old-style routes (e.g. `/askemos/:askemoId`, `/contacts`) predate module/project-scoped routing
 * (`/:module/:project/...`). These guards redirect them to their new-style equivalent instead of
 * letting them activate, resolving whichever module/project is appropriate first.
 */

function getRemainderSegments(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): string[] {
    const consumedSegments = route.pathFromRoot.flatMap(snapshot => snapshot.url.map(segment => segment.path));
    const [pathOnly] = state.url.split(/[?#]/);
    const allSegments = pathOnly.split('/').filter(Boolean);

    return allSegments.slice(consumedSegments.length);
}

/** Preserve deep-link query/fragment when rewriting legacy `/organization/...` URLs. */
function navigationExtrasFromState(router: Router, state: RouterStateSnapshot): { queryParams: Params; fragment?: string } {
    const urlTree = router.parseUrl(state.url);
    return {
        queryParams: urlTree.queryParams,
        fragment: urlTree.fragment ?? undefined,
    };
}

function buildModuleScopedRedirect(
    contextStateService: ContextStateService,
    moduleRouteContextService: ModuleRouteContextService,
    router: Router,
    newPathPrefix: string,
    remainder: string[],
    state: RouterStateSnapshot
): UrlTree {
    const slug = moduleRouteContextService.getSlugFromModuleType(contextStateService.effectiveModuleType());

    return router.createUrlTree(['/', slug, ...newPathPrefix.split('/'), ...remainder], navigationExtrasFromState(router, state));
}

function buildProjectScopedRedirect(
    contextStateService: ContextStateService,
    moduleRouteContextService: ModuleRouteContextService,
    router: Router,
    newPathPrefix: string,
    remainder: string[],
    state: RouterStateSnapshot
): UrlTree {
    const moduleType = contextStateService.effectiveModuleType();
    const slug = moduleRouteContextService.getSlugFromModuleType(moduleType);
    const module = contextStateService.enabledModules().find(m => m.type === moduleType);
    const lastProject = module?.projects.find(project => project.id === module.lastProjectId);
    const extras = navigationExtrasFromState(router, state);

    if (lastProject) {
        return router.createUrlTree(['/', slug, lastProject.urlSafeName, ...newPathPrefix.split('/'), ...remainder], extras);
    }

    return router.createUrlTree(['/', slug, 'home'], extras);
}

function buildAskemoRedirect(
    result: AskemoModuleAndProjectIdViewModel,
    moduleRouteContextService: ModuleRouteContextService,
    router: Router,
    askemoId: string,
    remainder: string[],
    state: RouterStateSnapshot
): UrlTree {
    const slug = moduleRouteContextService.getSlugFromModuleType(result.moduleType);

    return router.createUrlTree(
        ['/', slug, result.projectUrlSafeName, 'askemos', askemoId, ...remainder],
        navigationExtrasFromState(router, state)
    );
}

/**
 * Handles the old top-level `askemos` route: both the bare list (`/askemos`) and a specific
 * askemo (`/askemos/:askemoId/**`). A specific askemo has one fixed module/project, so it's
 * resolved via the backend rather than guessed from app state.
 */
export const legacyAskemoRedirectGuard: CanActivateFn = (route, state) => {
    const contextStateService = inject(ContextStateService);
    const contextApiService = inject(ContextApiService);
    const moduleRouteContextService = inject(ModuleRouteContextService);
    const router = inject(Router);

    return toObservable(contextStateService.isCurrentContextLoaded).pipe(
        filter(isLoaded => isLoaded),
        take(1),
        switchMap((): Observable<UrlTree> => {
            const remainder = getRemainderSegments(route, state);
            const askemoId = route.firstChild?.paramMap.get('askemoId');

            if (!askemoId) {
                return of(buildProjectScopedRedirect(contextStateService, moduleRouteContextService, router, 'askemos', remainder, state));
            }

            return contextApiService.getAskemoModuleAndProjectId(askemoId).pipe(
                map(result => buildAskemoRedirect(result, moduleRouteContextService, router, askemoId, remainder.slice(1), state)),
                catchError(() => of(router.createUrlTree(['/'])))
            );
        })
    );
};

/**
 * Old routes with a module-scoped (no project) new-style equivalent, e.g. `/contacts` -> `/:module/contacts`.
 *
 * Some old subtrees aren't a clean 1:1 rename of the path prefix (a deeper segment was renamed, or part of
 * the subtree has no new-style destination at all). `transformRemainder` lets a caller adjust the segments
 * that follow the matched old prefix before they're appended to the new one, or opt a specific sub-path out
 * of redirecting entirely by returning `null` (the old route then activates as-is, unredirected).
 */
export function createLegacyModuleScopedRedirectGuard(
    newPathPrefix: string,
    transformRemainder: (remainder: string[]) => string[] | null = remainder => remainder
): CanActivateFn {
    return (route, state) => {
        const contextStateService = inject(ContextStateService);
        const moduleRouteContextService = inject(ModuleRouteContextService);
        const router = inject(Router);

        return toObservable(contextStateService.isCurrentContextLoaded).pipe(
            filter(isLoaded => isLoaded),
            take(1),
            map((): boolean | UrlTree => {
                const remainder = transformRemainder(getRemainderSegments(route, state));
                if (remainder === null) {
                    return true;
                }

                return buildModuleScopedRedirect(contextStateService, moduleRouteContextService, router, newPathPrefix, remainder, state);
            })
        );
    };
}

/**
 * Old routes with a project-scoped new-style equivalent and no module-only alternative,
 * e.g. `/alerts` -> `/:module/:project/alerts`. Uses the effective module's last-used project;
 * falls back to the module home page when there isn't one.
 */
export function createLegacyProjectScopedRedirectGuard(newPathPrefix: string): CanActivateFn {
    return (route, state) => {
        const contextStateService = inject(ContextStateService);
        const moduleRouteContextService = inject(ModuleRouteContextService);
        const router = inject(Router);

        return toObservable(contextStateService.isCurrentContextLoaded).pipe(
            filter(isLoaded => isLoaded),
            take(1),
            map(() =>
                buildProjectScopedRedirect(
                    contextStateService,
                    moduleRouteContextService,
                    router,
                    newPathPrefix,
                    getRemainderSegments(route, state),
                    state
                )
            )
        );
    };
}
